import { useNavigate, useParams } from "react-router-dom";
import useArticlesService from "../../services/ArticlesService";
import { Article } from "../../types/Article";
import { FormEvent, useEffect, useState } from "react";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import useUrlUtilities from "../../utilities/UrlUtilities";
import NegativeButton from "../../components/NegativeButton";
import PositiveButton from "../../components/PositiveButton";
import SimpleAlert from "../../components/SimpleAlert";

export default function AdminArticlesDetailPage() {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const { createArticle, updateArticle, deleteArticle, getArticle } =
    useArticlesService();
  const [isLoading, setIsLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const { isValidUrl } = useUrlUtilities();
  const debounceDelay = 500;
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    url: "",
    isArchived: false,
    priority: 0,
    publicImageUrl: "",
    imageFile: null,
  });

  const deleteThisArticle = () => {
    setIsDeleting(true);
    deleteArticle(formData.id)
      .then(() => {
        navigate("/admin/articles");
      })
      .catch((error) => {
        console.error("Error deleting article:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title) {
      errors.title = "Title is required";
    }
    if (!formData.url) {
      errors.url = "URL is required";
    }
    if (formData.priority < 0) {
      errors.priority = "Priority must be a positive number";
    }

    // If the form is for a new article, ensure the image file is provided
    if (!formData.id && !formData.imageFile) {
      errors.imageFile = "Image file is required for new articles.";
    }

    if (formData.imageFile) {
      var imageFile = formData.imageFile as File;
      if (!imageFile.name.match(/\.(jpg|jpeg)$/)!) {
        errors.imageFile = "Image file must be a JPG or JPEG.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveArticle = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    // Create a new formdata and submit it.
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("Title", formData.title);
    formDataToSubmit.append("Url", formData.url);
    formDataToSubmit.append("IsArchived", formData.isArchived.toString());
    formDataToSubmit.append("Priority", formData.priority.toString());

    // Load the file as a blob.
    if (formData.imageFile) {
      const imageBlob = await new Promise<Blob>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const blob = new Blob([reader.result as ArrayBuffer]);
          resolve(blob);
        };
        reader.readAsArrayBuffer(formData.imageFile!);
      });
      formDataToSubmit.append("Image", imageBlob);
    }

    var savedArticle: Article | null = null;

    if (formData.id) {
      var updateResult = await updateArticle(formData.id, formDataToSubmit);

      savedArticle = updateResult as Article;

      // If the result is a http error
      if (!savedArticle) {
        alert("Error creating article");
        setIsSaving(false);
        return;
      }
    } else {
      var createResult = await createArticle(formDataToSubmit);
      savedArticle = createResult as Article;

      if (!savedArticle) {
        alert("Error creating article");
        setIsSaving(false);
        return;
      }
    }

    setFormData({
      ...formData,
      id: savedArticle.id,
      publicImageUrl: savedArticle.publicImageUrl,
    });

    setIsClean(true);
    setIsSaving(false);

    const cameFromNew = (articleId?.toLowerCase() === "new");

    if (cameFromNew) {
        navigate(`/admin/articles/${savedArticle.id}`, { replace: true });
    }
  };

  const isFormValid = submitted && Object.keys(validationErrors).length === 0;

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const handleArchivedChange = (e: any) => {
    const { checked } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      isArchived: checked,
    });
  };

  const handleImageFileChange = (e: any) => {
    const { files } = e.target;

    setIsClean(false);

    if (!files || files.length === 0) {
      setFormData({
        ...formData,
        imageFile: null,
      });

      setPreviewBlob(null);

      return;
    }

    setFormData({
    ...formData,
    imageFile: files[0],
    });

    // Load the image and set the preview blob.
    const reader = new FileReader();
    reader.onload = () => {
        const blob = new Blob([reader.result as ArrayBuffer]);
        setPreviewBlob(blob);
    };
    reader.readAsArrayBuffer(files[0]);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (initiated) {
      return;
    }

    setIsLoading(true);
    setInitiated(true);

    if (articleId?.toLowerCase() == "new") {
      setIsLoading(false);
    } else {
      getArticle(articleId as string)
        .then((result) => {
          var article = result as Article;
          setFormData({
            id: article.id,
            title: article.title,
            url: article.url,
            isArchived: article.isArchived,
            priority: article.priority,
            publicImageUrl: article.publicImageUrl,
            imageFile: null,
          });
        })
        .catch((error) => {
          console.error("Error fetching article:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initiated]);

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <div className="flex flex-row">
        <BackButton />
        <PageTitle className="text-4xl">
          {isLoading ? "Loading" : formData.id ? formData.title : "New Article"}
          {isLoading && <LoadingEllipsis />}
        </PageTitle>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-2 col-span-2">
          <h2 className="text-2xl">Details</h2>
          {isLoading && <Ghost additionalClasses="h-24 col-span-2 rounded" />}
          {!isLoading && formData && (
            <form onSubmit={saveArticle} className="form">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 xl:grid-cols-2">
                <div className="card p-8 flex flex-col gap-4">
                  <div className="">
                    <label htmlFor="title" className="block leading-6">
                      Article title *
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="title"
                      placeholder="Article title *"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "title"
                      )}`}
                    />

                    {validationError("title") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("title")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="url" className="block leading-6 pt-4">
                      Article URL *
                    </label>

                    <input
                      disabled={isSaving}
                      type="url"
                      name="url"
                      placeholder="Article url *"
                      value={formData.url}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "url"
                      )}`}
                    />

                    {validationError("url") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("url")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row items-left">
                    <input
                      disabled={isSaving}
                      type="checkbox"
                      name="isArchived"
                      id="isArchived"
                      checked={formData.isArchived}
                      onChange={handleArchivedChange}
                      className={`w-6 h-6 p-2 rounded-xl ${validStyle(
                        "isArchived"
                      )}`}
                    />
                    <label htmlFor="isArchived" className="ml-2">
                      Archived (article will not be shown to users)
                    </label>
                    {validationError("isArchived") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("isArchived")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="priority" className="block leading-6">
                      Priority (Higher priority will appear first)
                    </label>

                    <input
                      disabled={isSaving}
                      type="number"
                      name="priority"
                      placeholder="0"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "priority"
                      )}`}
                    />

                    {validationError("priority") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("priority")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="card p-8 flex flex-col gap-4">
                    {formData.publicImageUrl && !previewBlob && (
                      <div className="flex flex-col gap-2">
                        <img
                          src={formData.publicImageUrl}
                          alt="Public Article"
                          className="w-full h-auto rounded"
                        />
                      </div>
                    )}
                    {previewBlob && (
                      <div className="flex flex-col gap-2">
                        <img
                          src={URL.createObjectURL(previewBlob)}
                          alt="Public Article"
                          className="w-full h-auto rounded"
                        />
                      </div>
                    )}
                    <label htmlFor="imageFile" className="block leading-6 pt-4">
                        Article image (JPG or JPEG) *
                    </label>

                    <input
                      disabled={isSaving}
                      accept="image/jpg,image/jpeg"
                      type="file"
                      name="imageFile"
                      onChange={handleImageFileChange}
                      className={`w-full px-3 py-2 border rounded text-white ${validStyle(
                        "imageFile"
                      )}`}
                    />

                    {validationError("imageFile") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("imageFile")}
                      </div>
                    )}
                </div>
              </div>
              <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
                {formData.id && <NegativeButton
                  type="button"
                  disabled={isSaving}
                  className={`text-red-500 py-2 px-4 rounded border-red-900 border hover:bg-red-900/25 `}
                  onClick={() => setDeleteAlertOpen(true)}
                >
                  Delete article
                </NegativeButton>}
                <PositiveButton type="submit" disabled={isSaving || isClean}>
                  {isSaving ? "Saving..." : (formData.id && isClean) ? "Saved" : "Save changes"}
                </PositiveButton>
              </div>
            </form>
          )}
        </div>
      </div>
      <SimpleAlert
              open={deleteAlertOpen}
              setOpen={setDeleteAlertOpen}
              title={`Are you sure you want to delete this article?`}
              message="This action cannot be undone."
              confirmText={`Delete ${formData.title}`}
              onConfirm={deleteThisArticle}
            />
    </section>
  );
}
